import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { Button, Typography } from "@mui/material";

import BoostConfirmLocation from "./BoostConfirmLocation";
import BoostConfirmTotal from "./BoostConfirmTotal";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import ConfirmStripeEmbedCheckoutPopupBase from "@/components/stripe/embedCheckout/ConfirmStripeEmbedCheckoutPopup";
import { boostCampaignHelper } from "@/features/campaigns/digital/boost-campaign/utils/boostCampaignHelper";
import { stripeMicroService } from "@/shared/services/stripeMicroService";
import { BOOST_CAMP_PAY_TYPE, HTTP_STATUS } from "@/config/constants";
import { logger } from "@/helpers/loggerHelper";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";
import type { BoostSelection } from "./BoostConfirmLocation";

// import RpVirtualList from "@/components/virtual/RpVirtualList";

type BoostCampaignData = {
  id?: number | string;
  system_generated_campaign_id?: string;
  campaign_name?: string;
  boost_payment_method?: string;
  channelNames?: Array<{
    id: number | string;
    label: string;
  }>;
};

type BoostBrand = {
  id?: number | string;
  system_generated_brand_id?: string;
  brand_name?: string;
  is_white_label?: boolean | number;
};

type BoostUser = {
  id?: number | string;
  email?: string;
  first_name?: string;
  last_name?: string;
};

type BoostGlobalContext = {
  selectedBrand?: BoostBrand | null;
  loggedInUser?: BoostUser | null;
};

type BoostConfirmRequest = {
  locationData?: BoostSelection[];
  fk_campaign_id?: number | string;
  system_generated_campaign_id?: string;
  fk_campaign_name?: string;
  email_id?: string;
  user_id?: number | string;
  user_name: string;
  fk_brand_id?: number | string;
  system_generated_brand_id?: string;
  fk_brand_name?: string;
  payment_status: number;
  payment_method?: string;
  total_budget: number;
  return_url: string;
  is_only_PMax: number;
  is_white_label?: boolean | number;
};

type ConfirmBoostCampaignProps = {
  selections?: BoostSelection[];
  campaign?: BoostCampaignData | null;
  onMakeChange?: () => void;
  campaignHasOnlyPMaxChannel?: boolean;
};

const ConfirmStripeEmbedCheckoutPopup =
  ConfirmStripeEmbedCheckoutPopupBase as React.ComponentType<
    Record<string, any>
  >;

function ConfirmBoostCampaign({
  selections,
  campaign,
  onMakeChange,
  campaignHasOnlyPMaxChannel,
}: ConfirmBoostCampaignProps) {
  const navigate = useNavigate();

  const { selectedBrand, loggedInUser } = useContext(
    GlobalContext
  ) as BoostGlobalContext;
  const loader = useContext(LoaderContext);
  const [openPayment, setOpenPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loader(isLoading);
  }, [isLoading, loader]);

  useEffect(() => {
    return () => {
      loader(false);
    };
  }, [loader]);

  const total = useMemo(
    () => boostCampaignHelper.getBoostTotal(selections),
    [selections]
  );

  const reqData = useMemo<BoostConfirmRequest>(() => {
    return {
      locationData: selections,
      fk_campaign_id: campaign?.id,
      system_generated_campaign_id: campaign?.system_generated_campaign_id,
      fk_campaign_name: campaign?.campaign_name,
      email_id: loggedInUser?.email,
      user_id: loggedInUser?.id,
      user_name: `${loggedInUser?.first_name} ${loggedInUser?.last_name}`,
      fk_brand_id: selectedBrand?.id,
      system_generated_brand_id: selectedBrand?.system_generated_brand_id,
      fk_brand_name: selectedBrand?.brand_name,
      payment_status:
        campaign?.boost_payment_method === BOOST_CAMP_PAY_TYPE.STRIPE ? 0 : 1,
      payment_method: campaign?.boost_payment_method,
      total_budget: total,
      return_url: "/boost-campaign-return",
      is_only_PMax: campaignHasOnlyPMaxChannel ? 1 : 0,
      is_white_label: selectedBrand?.is_white_label,
    };
  }, [
    campaign,
    campaignHasOnlyPMaxChannel,
    loggedInUser,
    selectedBrand,
    selections,
    total,
  ]);

  const handleConfirm = useCallback(async () => {
    if (!selections?.length) {
      toastr.error("Error", "Please select at least one location");
      return;
    }

    if (campaign?.boost_payment_method === BOOST_CAMP_PAY_TYPE.STRIPE) {
      setOpenPayment(true);
    } else {
      let navigatedToThankYou = false;
      try {
        setIsLoading(true);
        const res = await campaignService.saveCampaignBoostBudget(reqData);
        if (res.status === HTTP_STATUS.OK) {
          navigatedToThankYou = true;
          setIsLoading(false);
          loader(false);
          grantThankYouAccessForFlow({
            queryType: "adjust_budget",
            stateFrom: "boost_campaign_confirm",
          });
          navigate(`${ROUTES.THANK_YOU}?type=adjust_budget`, {
            state: {
              from: "boost_campaign_confirm",
            },
          });
        }
      } catch (err) {
        logger.error(err);
      } finally {
        if (!navigatedToThankYou) {
          setIsLoading(false);
        }
      }
    }
  }, [campaign?.boost_payment_method, reqData, navigate, selections, loader]);

  return (
    <>
      <div>
        {/* <div
          style={{
            marginBottom: `${Math.ceil((selections?.length || 1) / 2.3)}rem`,
          }}
        >
          <RpVirtualList
            items={selections}
            renderItem={(loc) => (
              <BoostConfirmLocation
                key={loc?.locationId}
                loc={loc}
                channelsList={campaign?.channelNames}
              />
            )}
          />
        </div> */}

        {(campaignHasOnlyPMaxChannel
          ? selections?.slice(0, 1)
          : selections
        )?.map((loc) => (
          <BoostConfirmLocation
            key={loc?.locationId}
            loc={loc}
            channelsList={campaign?.channelNames}
            campaignHasOnlyPMaxChannel={campaignHasOnlyPMaxChannel}
            total={total}
          />
        ))}

        <BoostConfirmTotal selections={selections} />
        <div className="row my-5 align-items-center">
          <div className="col-md-6 mb-4 mb-md-0">
            <Typography variant="caption">
              Once 'Confirm' is selected, you can't make changes, your
              selections will be final for this campaign
            </Typography>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center flex-wrap justify-content-center justify-content-lg-end">
              <Button className="px-4 mr-1" onClick={onMakeChange}>
                Make changes
              </Button>
              <Button
                variant="contained"
                color="primary"
                className="px-5"
                onClick={handleConfirm}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      </div>

      {openPayment ? (
        <ConfirmStripeEmbedCheckoutPopup
          makePayment={openPayment}
          title="Payment"
          data={{
            email: loggedInUser!.email,
            amount: total,
            fk_brand_id: selectedBrand?.id,
            system_generated_brand_id: selectedBrand?.system_generated_brand_id,
            fk_brand_name: selectedBrand?.brand_name,
            ...reqData,
          }}
          onClose={() => setOpenPayment(false)}
          return_uri={`/boost-campaign-return`}
          api={stripeMicroService.boostCampaignPayment}
        />
      ) : null}
    </>
  );
}

export default ConfirmBoostCampaign;
